import React, { useState, useMemo } from "react";
import {
  Layout,
  Card,
  DatePicker,
  Typography,
  Space,
  Row,
  Col,
  Alert,
  Button,
  ConfigProvider,
} from "antd";
import { DashboardOutlined, CalendarOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { generateBiorhythmSeries } from "./utils/biorhythm";
import "antd/dist/reset.css";
import "./App.css";

const { Content } = Layout;
const { Title, Text } = Typography;

const App: React.FC = () => {
  const [birthDate, setBirthDate] = useState<Dayjs | null>(dayjs("1990-01-29"));
  const [targetDate, setTargetDate] = useState<Dayjs>(dayjs());

  const chartData = useMemo(() => {
    if (!birthDate || !targetDate) return [];
    return generateBiorhythmSeries(birthDate, targetDate, 15);
  }, [birthDate, targetDate]);

  const currentDayData = useMemo(() => {
    return chartData.find(
      (d) => d.fullDate === targetDate.format("YYYY-MM-DD"),
    );
  }, [chartData, targetDate]);

  const handlePreviousDay = () =>
    setTargetDate((prevDate) => prevDate.subtract(1, "day"));
  const handleToday = () => setTargetDate(dayjs());
  const handleNextDay = () =>
    setTargetDate((prevDate) => prevDate.add(1, "day"));

  const handleNextBirthday = () => {
    if (!birthDate) return;

    const today = dayjs();
    let nextBday = birthDate.year(today.year());

    if (nextBday.isBefore(today, "day")) {
      nextBday = nextBday.add(1, "year");
    }

    setTargetDate(nextBday);
  };

  const handleChartClick = (state: any) => {
    if (!state) return;

    let payloadData = null;
    if (state.activePayload && state.activePayload.length > 0) {
      payloadData = state.activePayload[0].payload;
    } else if (state.activeLabel) {
      payloadData = chartData.find((d) => d.date === state.activeLabel);
    }

    if (payloadData && payloadData.fullDate) {
      setTargetDate(dayjs(payloadData.fullDate));
    }
  };

  const handleDotClick = (props: any) => {
    if (props && props.payload && props.payload.fullDate) {
      setTargetDate(dayjs(props.payload.fullDate));
    }
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          colorPrimary: "#ff7043",
          borderRadius: 12,
          colorTextBase: "#424242",
        },
        components: {
          Card: {
            paddingLG: 32,
          },
        },
      }}
    >
      <Layout style={{ minHeight: "100vh", background: "transparent" }}>
        <Content
          style={{
            padding: "48px 24px",
            maxWidth: "1200px",
            margin: "0 auto",
            width: "100%",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: "40px" }}>
            <DashboardOutlined
              style={{
                fontSize: "48px",
                color: "#ff7043",
                marginBottom: "16px",
              }}
            />
            <Title level={1} className="gradient-text">
              SOFTY Biorhythm
            </Title>
            <Text type="secondary" style={{ fontSize: "16px" }}>
              Discover your physical, emotional, and intellectual cycles
            </Text>
          </div>

          <Row gutter={[32, 32]}>
            <Col xs={24} md={24} lg={8}>
              <Card className="glass-card" bordered={false}>
                <Space
                  direction="vertical"
                  size="large"
                  style={{ width: "100%" }}
                >
                  <div>
                    <Text strong style={{ fontSize: "16px" }}>
                      Date of Birth
                    </Text>
                    <DatePicker
                      size="large"
                      style={{
                        width: "100%",
                        marginTop: "8px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                      }}
                      value={birthDate}
                      onChange={(date) => setBirthDate(date)}
                      allowClear={false}
                      suffixIcon={
                        <CalendarOutlined style={{ color: "#ff7043" }} />
                      }
                    />
                  </div>
                  <div>
                    <Text strong style={{ fontSize: "16px" }}>
                      Target Date
                    </Text>
                    <DatePicker
                      size="large"
                      style={{
                        width: "100%",
                        marginTop: "8px",
                        marginBottom: "20px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                      }}
                      value={targetDate}
                      onChange={(date) => date && setTargetDate(date)}
                      allowClear={false}
                      suffixIcon={
                        <CalendarOutlined style={{ color: "#ff7043" }} />
                      }
                    />

                    <Space
                      wrap
                      size="small"
                      style={{ display: "flex", justifyContent: "center" }}
                    >
                      <Button onClick={handlePreviousDay} shape="round">
                        Previous Day
                      </Button>
                      <Button
                        onClick={handleToday}
                        type="primary"
                        shape="round"
                      >
                        Today
                      </Button>
                      <Button onClick={handleNextDay} shape="round">
                        Next Day
                      </Button>
                      <Button
                        onClick={handleNextBirthday}
                        shape="round"
                        disabled={!birthDate}
                      >
                        Next Birthday
                      </Button>
                    </Space>
                  </div>
                </Space>
              </Card>
            </Col>

            <Col xs={24} md={24} lg={16}>
              <Card
                className="glass-card"
                bordered={false}
                style={{ minHeight: "450px" }}
              >
                {!birthDate ? (
                  <div style={{ padding: "40px 0", textAlign: "center" }}>
                    <Alert
                      message="Welcome!"
                      description="Please select your Date of Birth to unveil your personal biorhythm chart."
                      type="warning"
                      showIcon
                      style={{
                        borderRadius: "16px",
                        backgroundColor: "#fff9c4",
                        border: "none",
                      }}
                    />
                  </div>
                ) : (
                  <div>
                    <div style={{ width: "100%", height: 400 }}>
                      <ResponsiveContainer>
                        <LineChart
                          data={chartData}
                          margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                          onClick={handleChartClick}
                          style={{ cursor: "pointer" }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#e0e0e0"
                          />
                          <XAxis
                            dataKey="date"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: "#757575", fontSize: 12 }}
                            dy={10}
                          />
                          <YAxis
                            domain={[0, 100]}
                            ticks={[0, 25, 50, 75, 100]}
                            tickFormatter={(value) => `${value}%`}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: "#757575", fontSize: 12 }}
                            dx={-10}
                          />
                          <Tooltip
                            formatter={(value: any, name: any) => [
                              `${value ?? 0}%`,
                              name ?? "",
                            ]}
                            contentStyle={{
                              borderRadius: "12px",
                              border: "none",
                              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                            }}
                          />
                          <Legend
                            verticalAlign="top"
                            height={48}
                            iconType="circle"
                            wrapperStyle={{ paddingBottom: "16px" }}
                          />
                          <ReferenceLine
                            y={50}
                            stroke="#bdbdbd"
                            strokeWidth={1}
                          />
                          <ReferenceLine
                            x={targetDate.format("MMM DD")}
                            stroke="#ff7043"
                            strokeDasharray="4 4"
                            strokeWidth={2}
                          />

                          <Line
                            type="monotone"
                            dataKey="physical"
                            name="Physical (23 days)"
                            stroke="#ff7043"
                            strokeWidth={3}
                            isAnimationActive={false}
                            dot={{
                              r: 14,
                              fill: "#fff",
                              fillOpacity: 0,
                              strokeWidth: 0,
                              onClick: handleDotClick,
                              cursor: "pointer",
                            }}
                            activeDot={{
                              r: 6,
                              onClick: handleDotClick,
                              cursor: "pointer",
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="emotional"
                            name="Emotional (28 days)"
                            stroke="#ec407a"
                            strokeWidth={3}
                            isAnimationActive={false}
                            dot={{
                              r: 14,
                              fill: "#fff",
                              fillOpacity: 0,
                              strokeWidth: 0,
                              onClick: handleDotClick,
                              cursor: "pointer",
                            }}
                            activeDot={{
                              r: 6,
                              onClick: handleDotClick,
                              cursor: "pointer",
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="intellectual"
                            name="Intellectual (33 days)"
                            stroke="#26a69a"
                            strokeWidth={3}
                            isAnimationActive={false}
                            dot={{
                              r: 14,
                              fill: "#fff",
                              fillOpacity: 0,
                              strokeWidth: 0,
                              onClick: handleDotClick,
                              cursor: "pointer",
                            }}
                            activeDot={{
                              r: 6,
                              onClick: handleDotClick,
                              cursor: "pointer",
                            }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    {currentDayData && (
                      <div style={{ textAlign: "center", marginTop: "12px" }}>
                        <Text type="secondary" style={{ fontSize: "13px" }}>
                          Selected Day Values:{" "}
                          <span style={{ color: "#ff7043", fontWeight: 500 }}>
                            Physical {currentDayData.physical}%
                          </span>{" "}
                          |{" "}
                          <span style={{ color: "#ec407a", fontWeight: 500 }}>
                            Emotional {currentDayData.emotional}%
                          </span>{" "}
                          |{" "}
                          <span style={{ color: "#26a69a", fontWeight: 500 }}>
                            Intellectual {currentDayData.intellectual}%
                          </span>
                        </Text>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </Content>
      </Layout>
    </ConfigProvider>
  );
};

export default App;
